import React, { useMemo, useState } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import {
  ChartBarIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TruckIcon,
  CubeIcon,
  MapPinIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  TrophyIcon
} from '@heroicons/react/24/outline'
import { useRecycling } from '../contexts/RecyclingContext'
import HKDistrictMap from '../components/HKDistrictMap'
import { useAuth } from '../contexts/AuthContext'

type SupplierTimeframe = 'current' | 'last' | 'quarter' | 'year'

const supplierNavigationItems = [
  { name: 'Dashboard', href: '/suppliers/dashboard', icon: ChartBarIcon },
  { name: 'Data Submission', href: '/suppliers/submission', icon: CloudArrowUpIcon },
  { name: 'Blockchain Verification', href: '/suppliers/verification', icon: ShieldCheckIcon },
  { name: 'Compliance Status', href: '/suppliers/compliance', icon: CheckCircleIcon }
]

const supplierTimeframeData: Record<SupplierTimeframe, {
  stats: Array<{
    name: string
    value: string
    change: string
    changeType: 'increase' | 'decrease'
    icon: typeof CubeIcon
  }>
  recentSubmissions: Array<{
    id: string
    type: string
    date: string
    status: string
    volume: string
    materials: string[]
    district: string
    credits: number
  }>
  complianceMetrics: Array<{
    metric: string
    score: number
    target: number
    status: string
  }>
}> = {
  current: {
    stats: [
      {
        name: 'PET Bottles Collected',
        value: '2,847 kg',
        change: '+15.3%',
        changeType: 'increase',
        icon: CubeIcon
      },
      {
        name: 'Compliance Score',
        value: '98%',
        change: '+2%',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Pending Submissions',
        value: '3',
        change: '-1',
        changeType: 'decrease',
        icon: DocumentTextIcon
      },
      {
        name: 'Verified Transactions',
        value: '247',
        change: '+12',
        changeType: 'increase',
        icon: ShieldCheckIcon
      }
    ],
    recentSubmissions: [
      {
        id: 'SUB-2025-001',
        type: 'Monthly PET Report',
        date: '2025-09-25',
        status: 'verified',
        volume: '850 kg',
        materials: ['PET Bottles (680kg)', 'Aluminium Cans (120kg)', 'Paper Cartons (50kg)'],
        district: 'Sha Tin',
        credits: 680
      },
      {
        id: 'SUB-2025-002',
        type: 'Weekly PET Report',
        date: '2025-09-22',
        status: 'processing',
        volume: '320 kg',
        materials: ['PET Bottles (260kg)', 'Glass Bottles (60kg)'],
        district: 'Kowloon City',
        credits: 260
      },
      {
        id: 'SUB-2025-003',
        type: 'Special PET Collection',
        date: '2025-09-20',
        status: 'pending',
        volume: '1,200 kg',
        materials: ['PET Bottles (950kg)', 'Aluminium Cans (180kg)', 'Paper Cartons (70kg)'],
        district: 'Central & Western',
        credits: 950
      },
      {
        id: 'SUB-2025-004',
        type: 'Weekly Collection',
        date: '2025-09-18',
        status: 'verified',
        volume: '445 kg',
        materials: ['PET Bottles (360kg)', 'Aluminium Cans (85kg)'],
        district: 'Wan Chai',
        credits: 360
      },
      {
        id: 'SUB-2025-005',
        type: 'Monthly Report',
        date: '2025-09-15',
        status: 'verified',
        volume: '780 kg',
        materials: ['PET Bottles (620kg)', 'Paper Cartons (90kg)', 'Glass Bottles (70kg)'],
        district: 'Eastern',
        credits: 620
      }
    ],
    complianceMetrics: [
      { metric: 'PET Data Accuracy', score: 99, target: 95, status: 'excellent' },
      { metric: 'Timeliness', score: 96, target: 90, status: 'excellent' },
      { metric: 'Documentation', score: 94, target: 90, status: 'good' },
      { metric: 'Verification Rate', score: 100, target: 95, status: 'excellent' }
    ]
  },
  last: {
    stats: [
      {
        name: 'PET Bottles Collected',
        value: '2,471 kg',
        change: '+8.2%',
        changeType: 'increase',
        icon: CubeIcon
      },
      {
        name: 'Compliance Score',
        value: '96%',
        change: '+1%',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Pending Submissions',
        value: '2',
        change: '-3',
        changeType: 'decrease',
        icon: DocumentTextIcon
      },
      {
        name: 'Verified Transactions',
        value: '235',
        change: '+8',
        changeType: 'increase',
        icon: ShieldCheckIcon
      }
    ],
    recentSubmissions: [
      {
        id: 'SUB-2025-098',
        type: 'Monthly PET Report',
        date: '2025-08-25',
        status: 'verified',
        volume: '780 kg',
        materials: ['PET Bottles (620kg)', 'Paper Cartons (90kg)', 'Glass (70kg)'],
        district: 'Tsuen Wan',
        credits: 620
      },
      {
        id: 'SUB-2025-099',
        type: 'Weekly PET Report',
        date: '2025-08-22',
        status: 'verified',
        volume: '295 kg',
        materials: ['PET Bottles (240kg)', 'Aluminium Cans (55kg)'],
        district: 'Kwun Tong',
        credits: 240
      }
    ],
    complianceMetrics: [
      { metric: 'PET Data Accuracy', score: 97, target: 95, status: 'excellent' },
      { metric: 'Timeliness', score: 94, target: 90, status: 'good' },
      { metric: 'Documentation', score: 92, target: 90, status: 'good' },
      { metric: 'Verification Rate', score: 98, target: 95, status: 'excellent' }
    ]
  },
  quarter: {
    stats: [
      {
        name: 'PET Bottles Collected',
        value: '8,142 kg',
        change: '+12.7%',
        changeType: 'increase',
        icon: CubeIcon
      },
      {
        name: 'Compliance Score',
        value: '97%',
        change: '+3%',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Pending Submissions',
        value: '1',
        change: '-8',
        changeType: 'decrease',
        icon: DocumentTextIcon
      },
      {
        name: 'Verified Transactions',
        value: '712',
        change: '+45',
        changeType: 'increase',
        icon: ShieldCheckIcon
      }
    ],
    recentSubmissions: [
      {
        id: 'SUB-2025-Q3-001',
        type: 'Quarterly PET Summary',
        date: '2025-09-30',
        status: 'processing',
        volume: '8,142 kg',
        materials: ['PET Bottles (6,500kg)', 'Aluminium Cans (1,200kg)', 'Paper Cartons (442kg)'],
        district: 'Multiple Districts',
        credits: 6500
      }
    ],
    complianceMetrics: [
      { metric: 'PET Data Accuracy', score: 98, target: 95, status: 'excellent' },
      { metric: 'Timeliness', score: 95, target: 90, status: 'excellent' },
      { metric: 'Documentation', score: 93, target: 90, status: 'good' },
      { metric: 'Verification Rate', score: 99, target: 95, status: 'excellent' }
    ]
  },
  year: {
    stats: [
      {
        name: 'PET Bottles Collected',
        value: '28,964 kg',
        change: '+18.3%',
        changeType: 'increase',
        icon: CubeIcon
      },
      {
        name: 'Compliance Score',
        value: '97%',
        change: '+4%',
        changeType: 'increase',
        icon: CheckCircleIcon
      },
      {
        name: 'Pending Submissions',
        value: '0',
        change: '-15',
        changeType: 'decrease',
        icon: DocumentTextIcon
      },
      {
        name: 'Verified Transactions',
        value: '2,847',
        change: '+312',
        changeType: 'increase',
        icon: ShieldCheckIcon
      }
    ],
    recentSubmissions: [
      {
        id: 'SUB-2025-ANNUAL',
        type: 'Annual PET Report',
        date: '2025-12-31',
        status: 'draft',
        volume: '28,964 kg',
        materials: ['PET Bottles (23,200kg)', 'Aluminium Cans (3,800kg)', 'Paper Cartons (1,500kg)', 'Glass (464kg)'],
        district: 'All Hong Kong',
        credits: 23200
      }
    ],
    complianceMetrics: [
      { metric: 'PET Data Accuracy', score: 98, target: 95, status: 'excellent' },
      { metric: 'Timeliness', score: 96, target: 90, status: 'excellent' },
      { metric: 'Documentation', score: 95, target: 90, status: 'excellent' },
      { metric: 'Verification Rate', score: 99, target: 95, status: 'excellent' }
    ]
  }
}

const SupplierNavigation: React.FC = () => {
  const location = useLocation()

  return (
    <div className="border-b border-gray-200 mb-8">
      <nav className="flex flex-wrap gap-4 sm:gap-8">
        {supplierNavigationItems.map((item) => {
          const isActive =
            location.pathname === item.href ||
            (item.href === '/suppliers/dashboard' && location.pathname === '/suppliers')

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                isActive
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-primary-600 hover:border-primary-200'
              }`}
            >
              <item.icon className="h-5 w-5 mr-2" />
              {item.name}
            </Link>
          )
        })}
      </nav>
    </div>
  )
}

const SuppliersPortal: React.FC = () => {
  return (
    <Layout portalType="suppliers">
      <Routes>
        <Route path="/" element={<SuppliersDashboard />} />
        <Route path="/dashboard" element={<SuppliersDashboard />} />
        <Route path="/submission" element={<DataSubmission />} />
        <Route path="/verification" element={<BlockchainVerification />} />
        <Route path="/compliance" element={<ComplianceStatus />} />
      </Routes>
    </Layout>
  )
}

const SuppliersDashboard: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<SupplierTimeframe>('current')
  const { user } = useAuth()
  const { getSupplierTotals, getSupplierSubmissions } = useRecycling()
  const supplierId = user?.id || ''
  const totals = getSupplierTotals(supplierId)
  const latestSubs = getSupplierSubmissions(supplierId).slice(0, 5)

  const currentData = supplierTimeframeData[selectedPeriod]
  const statAccentClasses = [
    'bg-primary-100 text-primary-600',
    'bg-primary-200 text-primary-700', 
    'bg-primary-100 text-primary-600',
    'bg-primary-100 text-primary-600'
  ]

  const getScoreColor = (score: number, target: number) => {
    if (score >= target + 5) return 'text-primary-600'
    if (score >= target) return 'text-primary-600'
    if (score >= target - 5) return 'text-primary-500'
    return 'text-primary-600'
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Shared Navigation */}
        <SupplierNavigation />
        
        {/* Corporate Client Badge */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-primary-100 to-primary-200 border border-primary-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="bg-primary-600 rounded-lg p-2 mr-3">
                <TruckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-primary-800">OneChain Corporate Client</h2>
                <p className="text-primary-600">Premium Supplier - PET Recycling Partner</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Supplier Dashboard</h1>
            <p className="text-gray-600">Track your PET bottle recycling performance and compliance status</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as SupplierTimeframe)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="current">Current Month</option>
              <option value="last">Last Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            <Link
              to="/suppliers/submission"
              className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 shadow-sm"
            >
              New Submission
            </Link>
          </div>
        </div>

        {/* Stats Grid (Original + Dynamic Plastic Credits) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {currentData.stats.map((stat, index) => (
            <div key={`${selectedPeriod}-${stat.name}`} className="bg-white rounded-lg shadow p-6 animate-fade-in">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${statAccentClasses[index % statAccentClasses.length]}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <div className="mt-1 flex items-baseline">
                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                    <p className={`ml-2 text-sm font-medium ${stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>{stat.change}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Last updated: 8/10/2025</p>
                </div>
              </div>
            </div>
          ))}
          {/* Dynamic Plastic Credits */}
          <div className="bg-white rounded-lg shadow p-6 animate-fade-in">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                <ShieldCheckIcon className="h-6 w-6" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Plastic Credits</p>
                <div className="mt-1 flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{totals.credits.toLocaleString()} PC</p>
                  <p className="ml-2 text-xs text-gray-500">1kg plastic = 1 credit</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">Last updated: 8/10/2025</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Submissions - Enhanced */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Recent Submissions
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">{latestSubs.length + currentData.recentSubmissions.length} total</span>
                  <Link to="/suppliers/submission" className="text-primary-600 hover:text-primary-700 text-sm font-medium px-3 py-1 bg-primary-50 rounded-lg transition-colors">
                    New Submission
                  </Link>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Show both actual submissions and fake data */}
                {[...latestSubs.slice(0, 2), ...currentData.recentSubmissions.slice(0, 3)].map((submission, index) => {
                  // Type-safe handling for mixed submission types
                  const isRealSubmission = 'materials' in submission && Array.isArray(submission.materials) && submission.materials.length > 0 && typeof submission.materials[0] === 'object' && 'category' in submission.materials[0]
                  const plasticKg = isRealSubmission 
                    ? (submission as any).materials.filter((m: any)=>m.category==='plastic').reduce((a: number,m: any)=>a+m.volumeKg,0)
                    : (submission as any).credits || 0
                  const allMaterials = isRealSubmission 
                    ? (submission as any).materials.map((m: any)=>`${m.name} (${m.volumeKg}kg)`).join(', ')
                    : (submission as any).materials.join(', ')
                  const district = (submission as any).district || 'N/A'
                  const displayDate = (submission as any).collectionDate || (submission as any).date
                  const displayType = (submission as any).type || (submission as any).reportType
                  const displayVolume = (submission as any).volume || `${(submission as any).materials?.reduce?.((a: number,m: any)=>a+(m.volumeKg||0),0) || 0} kg`
                  const hasTx = (submission as any).blockchainTx
                  
                  return (
                    <div key={`${selectedPeriod}-${submission.id}-${index}`} className="border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-primary-200 transition-all duration-200 animate-fade-in">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900">{displayType}</h4>
                            <div className={`px-3 py-1 text-xs font-medium rounded-full border ${
                              submission.status === 'verified' ? 'text-emerald-600 bg-emerald-50 border-emerald-200' :
                              submission.status === 'processing' ? 'text-blue-600 bg-blue-50 border-blue-200' :
                              submission.status === 'pending' ? 'text-yellow-600 bg-yellow-50 border-yellow-200' :
                              'text-red-600 bg-red-50 border-red-200'
                            }`}>
                              {submission.status}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mb-3">ID: {submission.id}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-emerald-600">{plasticKg} PC</div>
                          <div className="text-xs text-gray-500">Plastic Credits</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 font-medium">Date</p>
                          <p className="font-semibold">{displayDate}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-medium">District</p>
                          <p className="font-semibold text-primary-600">{district}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-medium">Total Volume</p>
                          <p className="font-semibold">{displayVolume}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-medium">Plastic Volume</p>
                          <p className="font-semibold text-primary-600">{plasticKg} kg</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-gray-500 font-medium text-sm mb-2">Materials Breakdown</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{allMaterials}</p>
                      </div>
                      
                      {submission.status === 'verified' && (hasTx || index < 2) && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center text-xs text-emerald-600">
                            <ShieldCheckIcon className="h-4 w-4 mr-1" />
                            <span className="font-mono">{hasTx || `0x${Math.random().toString(16).substr(2, 40)}`}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Compliance Metrics */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">PET Compliance Metrics</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {currentData.complianceMetrics.map((metric) => (
                  <div key={`${selectedPeriod}-${metric.metric}`} className="animate-fade-in">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-900">{metric.metric}</span>
                      <span className={`text-sm font-semibold ${getScoreColor(metric.score, metric.target)}`}>
                        {metric.score}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          metric.score >= metric.target ? 'bg-primary-500' : 'bg-primary-300'
                        }`}
                        style={{ width: `${Math.min(metric.score, 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Target: {metric.target}%</span>
                      <span className="capitalize text-primary-600">{metric.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const DataSubmission: React.FC = () => {
  const { addSubmission } = useRecycling()
  const { user } = useAuth()
  const [materials, setMaterials] = useState([
    { name: 'Plastic Bottles (PET)', category: 'plastic', volumeKg: '' },
    { name: 'Aluminium Cans', category: 'aluminium', volumeKg: '' },
    { name: 'Paper Drink Cartons', category: 'paper', volumeKg: '' },
    { name: 'Glass Bottles', category: 'glass', volumeKg: '' }
  ])
  const [formData, setFormData] = useState({ reportType: 'monthly', collectionDate: '', district: '', notes: '' })
  const [submitted, setSubmitted] = useState(false)

  const totalPlastic = useMemo(()=> materials.filter(m=>m.category==='plastic').reduce((a,m)=> a + (parseFloat(m.volumeKg)||0),0),[materials])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if(!user) return
    const prepared = materials.filter(m=>m.volumeKg && parseFloat(m.volumeKg) > 0).map((m,i)=>({
      id: 'MAT-' + i + '-' + Date.now(),
      category: m.category as any,
      name: m.name,
      volumeKg: parseFloat(m.volumeKg)
    }))
    addSubmission({
      supplierId: user.id,
      supplierName: user.organization || user.name,
      reportType: formData.reportType,
      collectionDate: formData.collectionDate,
      district: formData.district || 'Unknown',
      materials: prepared,
      notes: formData.notes
    })
    setSubmitted(true)
    setTimeout(()=> setSubmitted(false), 4000)
  }

  return (
    <div className="py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SupplierNavigation />
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Multi-Material Recycling Submission</h1>
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">New Recycling Report</h3>
              <p className="text-gray-600 mt-1">Only plastic volumes will convert to plastic credits (1kg = 1 credit)</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Plastic Credits Expected</p>
              <p className="text-xl font-semibold text-emerald-600">{totalPlastic} PC</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
                <select value={formData.reportType} onChange={e=>setFormData({...formData, reportType: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="weekly">Weekly Report</option>
                  <option value="monthly">Monthly Report</option>
                  <option value="special">Special Collection</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Collection Date</label>
                <input type="date" value={formData.collectionDate} onChange={e=>setFormData({...formData, collectionDate: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">HK District</label>
                <input type="text" placeholder="e.g. Sha Tin" value={formData.district} onChange={e=>setFormData({...formData, district: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Material Volumes (kg)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {materials.map((m,idx)=>(
                  <div key={m.name} className="border rounded-lg p-4 relative">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{m.name}</label>
                    <input type="number" min="0" value={m.volumeKg} onChange={e=>{
                      const copy=[...materials]; copy[idx]={...copy[idx], volumeKg: e.target.value}; setMaterials(copy)
                    }} placeholder="0" className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    {m.category==='plastic' && <span className="absolute top-2 right-2 text-emerald-600 text-[10px] font-semibold">counts</span>}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea rows={4} value={formData.notes} onChange={e=>setFormData({...formData, notes: e.target.value})} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Any additional context..." />
            </div>
            <div className="flex justify-end space-x-4">
              <button type="reset" onClick={()=>{setMaterials(materials.map(m=>({...m, volumeKg: ''}))); setFormData({reportType:'monthly', collectionDate:'', district:'', notes:''})}} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Reset</button>
              <button type="submit" className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2">
                <ArrowPathIcon className="h-4 w-4" />Submit for Verification
              </button>
            </div>
            {submitted && <div className="text-sm text-emerald-600 font-medium">Submission received! Pending verification.</div>}
          </form>
        </div>
      </div>
    </div>
  )
}

const BlockchainVerification: React.FC = () => {
  const { user } = useAuth()
  const { getSupplierSubmissions, verifySubmission } = useRecycling()
  const list = getSupplierSubmissions(user?.id || '').slice(0, 20)
  const [selectedTx, setSelectedTx] = useState<string | null>(null)
  
  // Add some fake blockchain data for better demo
  const enhancedList = list.length > 0 ? list : [
    {
      id: 'SUB-DEMO-001',
      materials: [{ id: '1', category: 'plastic' as const, name: 'PET Bottles', volumeKg: 340 }],
      collectionDate: '2025-09-28',
      district: 'Central & Western',
      status: 'verified' as const,
      blockchainTx: '0xa1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456',
      verifiedAt: '2025-09-28T14:30:00Z'
    },
    {
      id: 'SUB-DEMO-002', 
      materials: [{ id: '2', category: 'plastic' as const, name: 'PET Bottles', volumeKg: 280 }],
      collectionDate: '2025-09-26',
      district: 'Wan Chai',
      status: 'verified' as const,
      blockchainTx: '0xfedcba0987654321fedcba0987654321fedcba0987654321fedcba0987654321',
      verifiedAt: '2025-09-26T16:45:00Z'
    },
    {
      id: 'SUB-DEMO-003',
      materials: [{ id: '3', category: 'plastic' as const, name: 'PET Bottles', volumeKg: 520 }],
      collectionDate: '2025-09-24',
      district: 'Kowloon City',
      status: 'pending' as const
    }
  ]
  
  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SupplierNavigation />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Blockchain Verification</h1>
          <p className="text-gray-600">Immutable records of your plastic recycling contributions on the blockchain.</p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-emerald-600">{enhancedList.filter(s => s.status === 'verified').length}</div>
            <div className="text-sm text-emerald-700 font-medium">Verified Submissions</div>
          </div>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-blue-600">{enhancedList.reduce((sum, s) => sum + (s.materials?.filter(m=>m.category==='plastic').reduce((a,m)=>a+m.volumeKg,0) || 0), 0)}</div>
            <div className="text-sm text-blue-700 font-medium">Total Plastic (kg)</div>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-purple-600">{enhancedList.filter(s => s.blockchainTx).length}</div>
            <div className="text-sm text-purple-700 font-medium">Blockchain Records</div>
          </div>
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 text-center">
            <div className="text-2xl font-bold text-yellow-600">{enhancedList.filter(s => s.status === 'pending').length}</div>
            <div className="text-sm text-yellow-700 font-medium">Pending Verification</div>
          </div>
        </div>
        
        {/* Enhanced Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
            <h3 className="text-xl font-bold text-white flex items-center">
              <ShieldCheckIcon className="h-6 w-6 text-emerald-400 mr-2"/>
              Submission Verification Records
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    { label: 'Submission ID', width: 'w-32' },
                    { label: 'Date', width: 'w-28' },
                    { label: 'District', width: 'w-32' },
                    { label: 'Plastic (kg)', width: 'w-24' },
                    { label: 'Credits', width: 'w-24' },
                    { label: 'Status', width: 'w-28' },
                    { label: 'Blockchain Transaction', width: 'w-48' },
                    { label: 'Actions', width: 'w-24' }
                  ].map(h => (
                    <th key={h.label} className={`${h.width} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {enhancedList.map((sub, index) => {
                  const plastic = sub.materials?.filter(m=>m.category==='plastic').reduce((a,m)=>a+m.volumeKg,0) || 0
                  return (
                    <tr key={sub.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                      <td className="px-4 py-4 text-sm font-mono text-gray-800 font-medium">{sub.id}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{sub.collectionDate}</td>
                      <td className="px-4 py-4 text-sm text-gray-600 font-medium">{sub.district}</td>
                      <td className="px-4 py-4 text-sm text-primary-600 font-bold">{plastic}</td>
                      <td className="px-4 py-4 text-sm text-emerald-600 font-bold">{plastic}</td>
                      <td className="px-4 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          sub.status==='verified' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                          sub.status==='processing' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                          'bg-yellow-100 text-yellow-700 border border-yellow-200'
                        }`}>{sub.status}</span>
                      </td>
                      <td className="px-4 py-4 text-xs">
                        {sub.blockchainTx ? (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                {sub.blockchainTx.slice(0,8)}...{sub.blockchainTx.slice(-8)}
                              </span>
                              <button 
                                onClick={() => setSelectedTx(selectedTx === sub.id ? null : sub.id)}
                                className="text-blue-500 hover:text-blue-700 p-1 rounded"
                                title="View details"
                              >
                                🔍
                              </button>
                            </div>
                            {sub.verifiedAt && (
                              <div className="text-xs text-gray-500">
                                Verified: {new Date(sub.verifiedAt).toLocaleString()}
                              </div>
                            )}
                            {selectedTx === sub.id && (
                              <div className="mt-2 p-3 bg-gray-50 rounded border text-xs">
                                <p><strong>Full TX:</strong></p>
                                <p className="font-mono text-gray-700 break-all mb-2">{sub.blockchainTx}</p>
                                <p><strong>Block:</strong> #{12345678 + index}</p>
                                <p><strong>Gas Used:</strong> 21,000 gwei</p>
                                <p><strong>Network:</strong> Polygon PoS</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Awaiting verification</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {sub.status !== 'verified' && (
                          <button 
                            onClick={() => verifySubmission(sub.id)} 
                            className="text-xs bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                          >
                            Verify
                          </button>
                        )}
                        {sub.status === 'verified' && (
                          <div className="flex items-center text-emerald-600 text-xs">
                            <ShieldCheckIcon className="h-4 w-4 mr-1" />
                            Verified
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Showing {enhancedList.length} submissions</span>
              <span>All transactions are permanently recorded on the blockchain</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ComplianceStatus: React.FC = () => {
  const { user } = useAuth()
  const { getSupplierSubmissions, getDistrictTotals, topDistricts } = useRecycling()
  const subs = getSupplierSubmissions(user?.id || '')
  const totalPlastic = subs.reduce((a,s)=> a + s.materials.filter(m=>m.category==='plastic').reduce((x,m)=>x+m.volumeKg,0),0)
  const verifiedPlastic = subs.filter(s=>s.status==='verified').reduce((a,s)=> a + s.materials.filter(m=>m.category==='plastic').reduce((x,m)=>x+m.volumeKg,0),0)
  const verificationRate = totalPlastic? Math.round((verifiedPlastic/totalPlastic)*100) : 0
  const districts = getDistrictTotals()
  const top = topDistricts(3)
  
  // Add fake compliance data for better demo
  const complianceMetrics = [
    { name: 'Data Accuracy', score: 98, target: 95, status: 'excellent', trend: '+2%' },
    { name: 'Timeliness', score: 94, target: 90, status: 'good', trend: '+1%' },
    { name: 'Documentation Quality', score: 96, target: 90, status: 'excellent', trend: '+3%' },
    { name: 'Environmental Impact', score: 92, target: 85, status: 'excellent', trend: '+5%' },
    { name: 'Verification Rate', score: verificationRate, target: 95, status: verificationRate >= 95 ? 'excellent' : 'good', trend: '+8%' },
    { name: 'Regional Coverage', score: 88, target: 80, status: 'good', trend: '+12%' }
  ]
  
  const certifications = [
    { name: 'ISO 14001', status: 'active', validUntil: '2025-12-31', issuer: 'ISO International' },
    { name: 'Carbon Trust Standard', status: 'active', validUntil: '2025-10-15', issuer: 'Carbon Trust' },
    { name: 'HK Green Building Council', status: 'pending', validUntil: '2025-11-30', issuer: 'HKGBC' },
    { name: 'LEED Certification', status: 'active', validUntil: '2026-03-20', issuer: 'USGBC' }
  ]
  
  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SupplierNavigation />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Compliance & Impact Dashboard</h1>
          <p className="text-gray-600">Track your environmental compliance metrics and regional impact distribution.</p>
        </div>
        
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">Overall Score</span>
            </div>
            <div className="text-3xl font-bold text-emerald-600 mb-1">94.7%</div>
            <div className="text-sm text-emerald-700 font-medium">Compliance Rating</div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <CubeIcon className="h-8 w-8 text-blue-600" />
              <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-1">{totalPlastic}</div>
            <div className="text-sm text-blue-700 font-medium">kg Plastic Submitted</div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">{verificationRate}%</span>
            </div>
            <div className="text-3xl font-bold text-purple-600 mb-1">{verifiedPlastic}</div>
            <div className="text-sm text-purple-700 font-medium">kg Verified</div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <MapPinIcon className="h-8 w-8 text-yellow-600" />
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">{districts.length}</span>
            </div>
            <div className="text-3xl font-bold text-yellow-600 mb-1">{totalPlastic}</div>
            <div className="text-sm text-yellow-700 font-medium">Credits Earned</div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Enhanced Compliance Metrics */}
          <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
            <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <CheckCircleIcon className="h-6 w-6 text-emerald-600"/>Compliance Performance Matrix
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {complianceMetrics.map((metric) => {
                const percentage = (metric.score / 100) * 100
                const isExcellent = metric.status === 'excellent'
                return (
                  <div key={metric.name} className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-800">{metric.name}</span>
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-bold ${isExcellent ? 'text-emerald-600' : 'text-blue-600'}`}>
                          {metric.score}%
                        </span>
                        <span className="text-xs text-green-600 font-medium">{metric.trend}</span>
                      </div>
                    </div>
                    <div className="relative">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all duration-700 ${isExcellent ? 'bg-gradient-to-r from-emerald-400 to-green-500' : 'bg-gradient-to-r from-blue-400 to-indigo-500'}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="absolute top-0 left-0 w-full h-3 flex items-center">
                        <div 
                          className="w-0.5 h-4 bg-gray-400" 
                          style={{ left: `${(metric.target / 100) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Target: {metric.target}%</span>
                      <span className={`capitalize font-medium ${
                        isExcellent ? 'text-emerald-600' : 'text-blue-600'
                      }`}>{metric.status}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          
          {/* Certifications */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              <TrophyIcon className="h-6 w-6 text-yellow-600"/>Certifications
            </h3>
            <div className="space-y-4">
              {certifications.map((cert) => (
                <div key={cert.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-800 text-sm">{cert.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      cert.status === 'active' ? 'bg-green-100 text-green-700' :
                      cert.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>{cert.status}</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">Issued by: {cert.issuer}</p>
                  <p className="text-xs text-gray-500">Valid until: {cert.validUntil}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Enhanced Map Section */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <MapPinIcon className="h-6 w-6 text-blue-600"/>Hong Kong Regional Impact Map
            </h3>
            <div className="text-sm text-gray-500">{districts.length} districts contributing</div>
          </div>
          
          <div className="mb-6">
            <HKDistrictMap height="400px" />
            <p className="text-xs text-gray-500 mt-2 text-right">
              Map Data Source: Common Spatial Data Infrastructure (CSDI), Lands Department, HKSAR
            </p>
          </div>
          
          {/* District Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {districts.slice(0, 8).map(d => (
              <div key={d.district} className="bg-gray-50 border rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <p className="font-semibold text-gray-800 text-sm mb-2">{d.district}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plastic:</span>
                    <span className="font-semibold text-primary-600">{d.plasticKg} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Credits:</span>
                    <span className="font-semibold text-emerald-600">{d.credits} PC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Other:</span>
                    <span className="text-gray-500">{d.aluminiumKg + d.paperKg + d.glassKg} kg</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Top Performers */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-emerald-800 mb-4 flex items-center gap-2">
              <TrophyIcon className="h-5 w-5 text-yellow-500"/>Top Performing Districts
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {top.map((d, index) => {
                const medals = ['🥇', '🥈', '🥉']
                return (
                  <div key={d.district} className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <div className="text-2xl mb-2">{medals[index]}</div>
                    <h5 className="font-semibold text-gray-800 mb-1">{d.district}</h5>
                    <p className="text-sm text-emerald-600 font-medium">{d.plasticKg} kg plastic</p>
                    <p className="text-xs text-gray-600">{d.credits} plastic credits</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SuppliersPortal
